import { motion } from "framer-motion";
import { Award, BookOpen, Sparkles } from "lucide-react";
import { ABOUT } from "@/constants/testIds";

const founders = [
  {
    id: ABOUT.founderKetan,
    name: "Ketan Shah",
    role: "Founder · Wealth Architect",
    badge: "Since 2008",
    icon: Award,
    description:
      "The anchor of Aarkays. Has personally guided 2000+ families through every market cycle since 2008 — bull runs, crashes, and everything in between.",
    img: "https://images.pexels.com/photos/8867434/pexels-photo-8867434.jpeg?auto=compress&cs=tinysrgb&w=600",
    accent: "primary",
  },
  {
    id: ABOUT.founderSanyam,
    name: "Sanyam Shah",
    role: "Chartered Accountant",
    badge: "Joined 2022",
    icon: BookOpen,
    description:
      "Brings tax-efficient structuring and a fresh, research-driven approach to portfolio construction for the next generation of investors.",
    img: "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=600",
    accent: "secondary",
  },
  {
    id: ABOUT.founderRiddhi,
    name: "Riddhi Shah",
    role: "Chartered Accountant",
    badge: "Joined 2022",
    icon: Sparkles,
    description:
      "Riddhi brings deep listening to actually solve your financial problems — while giving you the comfort and trust that your money is in the right hands. Her advice, communication and reliability stand out; she goes the extra mile for the satisfaction of every client.",
    img: "https://images.pexels.com/photos/3760069/pexels-photo-3760069.jpeg?auto=compress&cs=tinysrgb&w=600",
    accent: "secondary",
  },
];

export default function About() {
  return (
    <section
      data-testid={ABOUT.section}
      id="about"
      className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32"
    >
      <div className="grid md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-5">
          <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium">
            Our People
          </span>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-primary mt-3 leading-[1.1]">
            Pool of <em className="not-italic text-secondary">experience</em>,<br />
            spirit of fresh ideas.
          </h2>
        </div>
        <div className="md:col-span-7 md:pt-4">
          <p className="text-lg text-foreground/75 leading-relaxed">
            Aarkays Consultancy was founded by <strong className="text-primary">Mr. Ketan Shah</strong> in 2008,
            who has been managing wealth for over 2,000+ clients for nearly two decades. In 2022,
            two Chartered Accountants — <strong className="text-primary">Sanyam Shah</strong> and{" "}
            <strong className="text-primary">Riddhi Shah</strong> — joined him, bringing a deep pool
            of knowledge, fresh ideas and innovation to make finance easy for the common person.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {founders.map((f, i) => (
          <motion.article
            key={f.id}
            data-testid={f.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.6 }}
            className="bg-card rounded-3xl p-8 border border-border hover:shadow-md hover:-translate-y-1 transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 grid place-items-center text-primary">
                <f.icon className="w-6 h-6" />
              </div>
              <span className="text-[10px] uppercase tracking-widest bg-secondary/10 text-secondary px-3 py-1 rounded-full">
                {f.badge}
              </span>
            </div>
            <h3 className="font-serif text-2xl text-primary">{f.name}</h3>
            <p className="text-sm uppercase tracking-wider text-muted-foreground mt-1">{f.role}</p>
            <p className="text-sm text-foreground/75 leading-relaxed mt-5">{f.description}</p>
          </motion.article>
        ))}
      </div>

      {/* Purpose / Passion / Vision */}
      <div className="mt-20 grid md:grid-cols-3 gap-6">
        {[
          { t: "Our Purpose", d: "To help families build wealth steadily and protect it from emotion-driven mistakes." },
          { t: "Our Passion", d: "Listening to people, understanding their pain points, and guiding them to clarity." },
          { t: "Our Vision", d: "A generation that invests with confidence — through behaviour, not noise." },
        ].map((b, i) => (
          <motion.div
            key={b.t}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="border-l-2 border-secondary pl-6 py-2"
          >
            <h4 className="font-serif text-xl text-primary">{b.t}</h4>
            <p className="text-foreground/70 mt-2 leading-relaxed">{b.d}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
